import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getEngine, warmupEngine, webgpuSupported } from "../../utils/webllmengine";
import { summarizeWithEngine } from "../../utils/summarize"; // we'll write this next

const WebLLMContext = createContext(null);

export function WebLLMProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [progressText, setProgressText] = useState("");
  const [progress, setProgress] = useState(0);
  const engineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!webgpuSupported()) {
        setError("WebGPU not available");
        return;
      }
      try {
        // Kick engine load early
        const engine = await getEngine();
        if (cancelled) return;

        engineRef.current = engine;

        // Optional: warm up
        await warmupEngine();
        if (cancelled) return;

        setReady(true);
      } catch (e) {
        console.error("[WebLLMProvider] init error:", e);
        setError(e.message || "Engine failed");
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Summarize helper everyone can call
  const summarize = useMemo(() => {
    return async function summarize(text) {
      if (!engineRef.current) throw new Error("Engine not ready");
      return summarizeWithEngine(engineRef.current, text);
    };
  }, []);

  const value = { ready, error, progress, progressText, summarize };
  return <WebLLMContext.Provider value={value}>{children}</WebLLMContext.Provider>;
}

export function useWebLLM() {
  const ctx = useContext(WebLLMContext);
  if (!ctx) throw new Error("useWebLLM must be used inside <WebLLMProvider>");
  return ctx;
}
